import { action, observable, makeObservable } from 'mobx';
import { ResizeObserver } from '@juggle/resize-observer';

import {ServiceBase} from '../ServiceBase';
import {IHeaderService} from './header.service.interface';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import { DomEventSubscription } from '../../../utils/event-listener-registration';
import {IMenuItemRenderer} from '../menu-items-common/renderer/menu-item.renderer.interface';
import {CrossBrandsMenuItemConfigReader} from '../cross-brands/cross-brands-item-config-reader';
import {GenericCrossBrandsMenuItemRenderer} from './menu-items/generic-cross-brands-menu-item.renderer';
import {MenuItemKey} from '../configurations/config-data-sections/generated-config-const';

export class HeaderService extends ServiceBase implements IHeaderService {
    private _headerResizeObserver: ResizeObserver | null = null;
    private _onScrollSubscription: DomEventSubscription | null = null;
    private _headerElement: HTMLElement | null = null;
    private _headerRegulationDataElement: HTMLElement | null = null;

    @observable.ref
    _headerHeight: number = 0;
    @observable.ref
    headerRegulationHeight: number = 0;

    private readonly menuItemConfigReader: CrossBrandsMenuItemConfigReader;
  
    constructor(services: IServiceFactoryExtended) {
        super(services);
        makeObservable(this);
        this._headerResizeObserver = new ResizeObserver(this._onHeaderResize);
        this._onScrollSubscription = this.services.window.subscribeTo.scroll(this._onWindowScroll);

        this.menuItemConfigReader = new CrossBrandsMenuItemConfigReader(services);
    }

    get headerHeight(): number {
        return this._headerHeight + this.headerRegulationHeight;
    }

    @action
    private computeHeaderHeight() {
        this._headerHeight = this._headerElement?.offsetHeight || 0;
    }

    @action
    private computeRegulationHeaderHeight() {
        this.headerRegulationHeight = this._headerRegulationDataElement?.offsetHeight || 0;
    }

    private _onHeaderResize = () => {
        this.computeHeaderHeight();
        this.computeRegulationHeaderHeight();
    };

    private _onWindowScroll = () => {
        this.computeHeaderHeight();
        this.computeRegulationHeaderHeight();
    };

    @action
    setHeaderElement(headerElement: HTMLElement): void {
        if(this._headerElement) {
            this._headerResizeObserver.unobserve(this._headerElement);
        }
        this._headerElement = headerElement;
        this.computeHeaderHeight();
        if (headerElement){
            this._headerResizeObserver.observe(headerElement);
        }
    }

    @action
    setHeaderRegulationDataElement(headerRegulationDataElement: HTMLElement): void {
        if(this._headerRegulationDataElement) {
            this._headerResizeObserver.unobserve(this._headerRegulationDataElement);
        }
        this._headerRegulationDataElement = headerRegulationDataElement;
        this.computeRegulationHeaderHeight();
        if (headerRegulationDataElement){
            this._headerResizeObserver.observe(headerRegulationDataElement);
        }
    }

    getHeaderRegulationDataElement(): HTMLElement | null {
        return this._headerRegulationDataElement;
    }

    getCrossBrandsMenuItems(): IMenuItemRenderer[] {
        const crossBrandsListConfig = this.services.configuration.data.header.cross_brand;

        return crossBrandsListConfig.map(crossBrandKey => {
            return this.getCrossBrandItemRenderer(crossBrandKey);
        }).filter(menuItem => {
            return menuItem.display;
        });
    }
    private getCrossBrandItemRenderer(crossBrandKey: MenuItemKey) {
        let menuItemRenderer = this.services.regulation.current.menuItems.getAppHeaderMenuItem(crossBrandKey);
        if(menuItemRenderer) {
            return menuItemRenderer;
        }
        return new GenericCrossBrandsMenuItemRenderer(crossBrandKey, this.menuItemConfigReader, this.services);
    }

    dispose(): void {
        this._headerResizeObserver.disconnect();
        this._headerResizeObserver = null;
        this._onScrollSubscription.dispose();
        this._onScrollSubscription = null;
    }
}
